
import { DateTime } from "luxon";
import { Car, Cars, Rate } from "@/types/coral-types";
import reservation_state from "@/contexts/reservation_state";
import { NextPaymentMethod } from "@/types/reservation";

interface ViewItemListEvent extends Record<string, unknown> {
  event: string;
  login: boolean;
  ecommerce: {
    item_list_id: string;
    item_list_name: string;
    currency: string;
    segmento: string;
    franquia: string;
    plano_pagamento: string;
    data_retirada: string;
    data_entrega: string;
    periodo: number;
    local_retirada: string;
    local_entrega: string;
    coupon: string;
    items: ViewItem[];
  };
}

interface AddToCartEvent extends Record<string, unknown> {
  event: string;
  login: boolean;
  ecommerce: {
    currency: string;
    value: number;
    segmento: string;
    franquia: string;
    plano_pagamento: string;
    data_retirada: string;
    data_entrega: string;
    periodo: number;
    local_retirada: string;
    local_entrega: string;
    coupon: string;
    items: ViewItem[];
  };
}

export interface ViewItem {
  item_id: string;
  item_name: string;
  affiliation: string;
  coupon: string;
  discount: number;
  index: number;
  item_brand: string;
  item_category: string;
  item_category2: string;
  item_category3: string;
  item_category4: string;
  item_category5: string;
  item_category6: string;
  item_category7: string;
  item_category8: string;
  item_category9: string;
  item_category10: string;
  item_category11?: string;
  item_list_id: string;
  item_list_name: string;
  item_variant: string;
  location_id: string;
  currency: string;
  metric1: number;
  metric2: number;
  metric3: number;
  metric4: number;
  price: number;
  quantity: number;
}

export function formatSimilarCar(similarCar: string): string {
  let formatted = similarCar.replace(/,/g, '');
  formatted = formatted.replace(/(\s+\S+)\s/g, '$1|');
  return formatted;
}

export function formatLuggage(luggageQuantity: number, singular: string, plural: string): string {
  const quantity = Number(luggageQuantity) || 0;
  return `${quantity} ${quantity === 1 ? singular : plural}`;
}

export function calculateQuantity(pickupDate: string, returnDate: string): number {
  try {
    const start = DateTime.fromISO(pickupDate, { zone: "America/Sao_Paulo" });
    const end = DateTime.fromISO(returnDate, { zone: "America/Sao_Paulo" });
    if (!start.isValid || !end.isValid) {
      return 1;
    }
    const diffDays = Math.floor(end.diff(start, 'days').days);
    return Math.max(1, diffDays);
  } catch (e) {
    return 1;
  }
}

export function sendViewItemEvent(depot: any, query: any, vehicles: Cars, login: boolean): void {
  const paymentMethodFromState: NextPaymentMethod = reservation_state.paymentMethod;
  let rateKeyForViewItem: keyof Rate;

  if (paymentMethodFromState === 'creditCard') {
    rateKeyForViewItem = 'cartao';
  } else if (paymentMethodFromState === 'pospaid') {
    rateKeyForViewItem = 'pospago';
  } else {
    rateKeyForViewItem = 'pix';
  }

  const itemListId = `RT|${query.pickup_store || ''}-${query.return_store || ''}|${query.pickup_date || ''}-${query.return_date || ''}|${query.pickup_time || ''}-${query.return_time || ''}`;
  const periodo = calculateQuantity(query.pickup_date as string, query.return_date as string);

  const items: ViewItem[] = vehicles.map((vehicle): ViewItem | null => {
    const rateDetailForCurrentMethod = vehicle.rate?.[rateKeyForViewItem];
    const pospagoRateDetail = vehicle.rate?.pospago;

    let priceCurrentMethodTotal: number;
    if (paymentMethodFromState === 'pix' || paymentMethodFromState === 'creditCard') {
      priceCurrentMethodTotal = Number(rateDetailForCurrentMethod?.pre_paid_price ?? rateDetailForCurrentMethod?.price ?? 0);
    } else {
      priceCurrentMethodTotal = Number(rateDetailForCurrentMethod?.price ?? 0);
    }
    const pricePospagoTotal = Number(pospagoRateDetail?.price ?? 0);

    const dailyPriceCurrentMethod = periodo > 0 ? priceCurrentMethodTotal / periodo : 0;
    const dailyPricePospago = periodo > 0 ? pricePospagoTotal / periodo : 0;

    const discountValuePerDay = (paymentMethodFromState === "pix" || paymentMethodFromState === "creditCard") && pospagoRateDetail
      ? Math.max(0, parseFloat((dailyPricePospago - dailyPriceCurrentMethod).toFixed(2)))
      : 0;

    return {
      item_id: vehicle.main_car ?? ``,
      item_name: `Grupo ${vehicle.group || ''}|${formatSimilarCar(vehicle.similar_car || '')}`,
      affiliation: "Reserva|Tradicional",
      coupon: (query.coupon as string) || '',
      discount: discountValuePerDay,
      index: Number(vehicle.id) || 0,
      item_brand: formatSimilarCar(vehicle.similar_car || ''),
      item_category: `Grupo ${vehicle.group || ''}`,
      item_category2: `${vehicle.n_doors || ''} Portas`,
      item_category3: `${vehicle.n_seats || ''} Passageiros`,
      item_category4: vehicle.steering_gear || '',
      item_category5: formatLuggage(vehicle.trunk_big_luggage, 'Mala Grande', 'Malas Grandes'),
      item_category6: vehicle.transmission || '',
      item_category7: vehicle.air_conditioning ? "Ar Condicionado" : "Sem Ar Condicionado",
      item_category8: vehicle.main_car || "",
      item_category9: '',
      item_category10: vehicle.availability === 'Available' ? "Disponível" : "Indisponível",
      item_category11: (paymentMethodFromState === "pix" || paymentMethodFromState === "creditCard") ? "Pagar Agora" : "Pagar Depois",
      item_list_id: itemListId,
      item_list_name: `RT|${query.pickup_store || ''}-${query.return_store || ''}|Busca`,
      item_variant: "Valor Diária",
      location_id: depot?.location_id || depot?.coral_code || '',
      currency: "BRL",
      price: parseFloat(dailyPriceCurrentMethod.toFixed(2)),
      quantity: periodo,
      metric1: parseFloat(priceCurrentMethodTotal.toFixed(2)),
      metric2: parseFloat(pricePospagoTotal.toFixed(2)),
      metric3: parseFloat(dailyPricePospago.toFixed(2)),
      metric4: parseFloat(dailyPriceCurrentMethod.toFixed(2)),
    };
  }).filter((item): item is ViewItem => item !== null);

  const viewItemListEvent: ViewItemListEvent = {
    event: "view_item_list",
    login,
    ecommerce: {
      item_list_id: itemListId,
      item_list_name: `RT|${query.pickup_store || ''}-${query.return_store || ''}|Busca`,
      currency: "BRL",
      segmento: "rac",
      franquia: "0",
      plano_pagamento: "diaria",
      data_retirada: (query.pickup_date as string) || '',
      data_entrega: (query.return_date as string) || '',
      periodo: periodo,
      local_retirada: (query.pickup_store as string) || '',
      local_entrega: (query.return_store as string) || '',
      coupon: (query.coupon as string) || '',
      items: items
    }
  };
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(viewItemListEvent);
}

export function sendAddToCartEvent(paymentMethod: NextPaymentMethod, depot: any, query: any, vehicle: Car, login: boolean) {
  let rateKeyForAddToCart: keyof Rate;
  if (paymentMethod === 'creditCard') {
    rateKeyForAddToCart = 'cartao';
  } else if (paymentMethod === 'pospaid') {
    rateKeyForAddToCart = 'pospago';
  } else {
    rateKeyForAddToCart = 'pix';
  }

  const itemListId = `RT|${query.pickup_store || ''}-${query.return_store || ''}|${query.pickup_date || ''}-${query.return_date || ''}|${query.pickup_time || ''}-${query.return_time || ''}`;
  const periodo = calculateQuantity(query.pickup_date as string, query.return_date as string);

  const rateDetailForSelectedMethod = vehicle.rate?.[rateKeyForAddToCart];
  const pospagoRateDetail = vehicle.rate?.pospago;

  if (!rateDetailForSelectedMethod) {
    return;
  }

  let priceForSelectedMethodTotal: number;
  if (paymentMethod === 'pix' || paymentMethod === 'creditCard') {
    priceForSelectedMethodTotal = Number(rateDetailForSelectedMethod.pre_paid_price ?? rateDetailForSelectedMethod.price ?? 0);
  } else {
    priceForSelectedMethodTotal = Number(rateDetailForSelectedMethod.price ?? 0);
  }

  const pricePospagoTotal = Number(pospagoRateDetail?.price ?? 0);
  const ecommerceValue = priceForSelectedMethodTotal;

  const dailyPriceForSelectedMethod = periodo > 0 ? priceForSelectedMethodTotal / periodo : 0;
  const dailyPricePospago = periodo > 0 ? pricePospagoTotal / periodo : 0;

  const discountValuePerDay = (paymentMethod === "pix" || paymentMethod === "creditCard") && pospagoRateDetail && rateDetailForSelectedMethod
    ? Math.max(0, parseFloat((dailyPricePospago - dailyPriceForSelectedMethod).toFixed(2)))
    : 0;

  const selectedItem: ViewItem = {
    item_id: vehicle.main_car ?? ``,
    item_name: `Grupo ${vehicle.group || ''}|${formatSimilarCar(vehicle.similar_car || '')}`,
    affiliation: "Reserva|Tradicional",
    coupon: (query.coupon as string) || '',
    discount: discountValuePerDay,
    index: Number(vehicle.id) || 0,
    item_brand: formatSimilarCar(vehicle.similar_car || ''),
    item_category: `Grupo ${vehicle.group || ''}`,
    item_category2: `${vehicle.n_doors || ''} Portas`,
    item_category3: `${vehicle.n_seats || ''} Passageiros`,
    item_category4: vehicle.steering_gear || '',
    item_category5: formatLuggage(vehicle.trunk_big_luggage, 'Mala Grande', 'Malas Grandes'),
    item_category6: vehicle.transmission || '',
    item_category7: vehicle.air_conditioning ? "Ar Condicionado" : "Sem Ar Condicionado",
    item_category8: vehicle.main_car || "",
    item_category9: '',
    item_category10: vehicle.availability === 'Available' ? "Disponível" : "Indisponível",
    item_category11: (paymentMethod === "pix" || paymentMethod === "creditCard") ? "Pagar Agora" : "Pagar Depois",
    item_list_id: itemListId,
    item_list_name: `RT|${query.pickup_store || ''}-${query.return_store || ''}|Busca`,
    item_variant: "Valor Diária",
    location_id: depot?.location_id || depot?.coral_code || '',
    currency: "BRL",
    price: parseFloat(dailyPriceForSelectedMethod.toFixed(2)),
    quantity: periodo,
    metric1: parseFloat(priceForSelectedMethodTotal.toFixed(2)),
    metric2: parseFloat(pricePospagoTotal.toFixed(2)),
    metric3: parseFloat(dailyPricePospago.toFixed(2)),
    metric4: parseFloat(dailyPriceForSelectedMethod.toFixed(2)),
  };

  const selectItemEvent: AddToCartEvent = {
    event: "add_to_cart",
    login,
    ecommerce: {
      currency: "BRL",
      value: parseFloat(ecommerceValue.toFixed(2)),
      segmento: (query.segmento as string) || 'rac',
      franquia: '0',
      plano_pagamento: 'diaria',
      data_retirada: (query.pickup_date as string) || '',
      data_entrega: (query.return_date as string) || '',
      periodo: periodo,
      local_retirada: (query.pickup_store as string) || '',
      local_entrega: (query.return_store as string) || '',
      coupon: (query.coupon as string) || '',
      items: [selectedItem]
    }
  };

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(selectItemEvent);
}
